<template>
  <b-modal
    ref="modal"
    title="Thêm sản phẩm vào danh mục chỉ tiêu"
    size="xl"
    hide-footer
    :no-close-on-backdrop="isProcessing"
    :no-close-on-esc="isProcessing"
    :hide-header-close="isProcessing"
    @shown="focusFirstInput"
    @hidden="reset"
  >
    <!-- SRS 10.2 "Cách thêm": theo nhánh (mặc định) / từng sản phẩm / chuyển toàn bộ (FR-211). -->
    <b-form-group label="Cách thêm" label-class="font-weight-bold pb-1" class="mb-3">
      <b-form-radio-group
        v-model="mode"
        :options="modeOptions"
        :disabled="isProcessing"
        @change="clearPreview"
      ></b-form-radio-group>
    </b-form-group>
    <b-row>
      <b-col v-if="isCategoryMode" cols="12" lg="6" class="mb-3">
        <label class="font-weight-bold mb-1"
          >Chọn danh mục sản phẩm
          <span class="font-weight-normal text-muted font-size-sm"
            >(lấy cả các danh mục sản phẩm con)</span
          ></label
        >
        <!-- Cây có sẵn của D-Core (`category/get-list-category`, như InventoryCheckAddModal);
             `ref` đặt ở div vì b-col là functional component, Vue 2 không gắn ref được. -->
        <div ref="firstInput">
          <treeselect
            :value="categoryId"
            :options="categoryOptions"
            :normalizer="categoryNormalizer"
            :searchable="true"
            :clearable="true"
            :disabled="isProcessing"
            :is-default-expanded="false"
            :placeholder="categoriesLoading ? 'Đang tải...' : 'Chọn danh mục sản phẩm'"
            noResultsText="Không có kết quả"
            noOptionsText="Không có kết quả"
            loadingText="Đang tải..."
            openDirection="bottom"
            @input="onCategoryInput"
          />
        </div>
        <!-- UX (người dùng chốt 30/09/2026): nhãn ngắn + icon (i); câu SRS 10.2 đầy đủ ở tooltip. -->
        <div class="d-flex align-items-center mt-2">
          <b-form-checkbox v-model="rememberCategory" :disabled="isProcessing"
            >Ghi nhớ danh mục này</b-form-checkbox
          >
          <b-button
            v-b-tooltip.hover.focus
            variant="link"
            size="sm"
            class="p-0 ml-2"
            :title="REMEMBER_HINT"
            :aria-label="REMEMBER_HINT"
            ><i class="flaticon2-information icon-md text-muted"></i
          ></b-button>
        </div>
      </b-col>
      <b-col v-else-if="isProductMode" cols="12" lg="6" class="mb-3">
        <label class="font-weight-bold mb-1">Chọn từng sản phẩm</label>
        <!-- AutoSuggest dùng chung tự thêm chữ "Nhập " trước placeholder → "Nhập để tìm kiếm...". -->
        <div ref="firstInput">
          <AutoSuggest
            :model.sync="searchText"
            :suggestions="suggestions"
            suggestion-name="productName"
            placeholder="để tìm kiếm..."
            :disabled="isProcessing"
            @change="onSearchInput"
            @select="onSelectProduct"
          >
            <template #custom="{ suggestion }">
              <span class="text-break">
                {{ suggestion.item.productCode }} - {{ suggestion.item.productName }}
              </span>
            </template>
            <template #after-section>
              <div v-if="searchState === 'empty'" class="px-3 py-2 text-muted">
                Không tìm thấy sản phẩm
              </div>
            </template>
          </AutoSuggest>
        </div>
        <b-form-text v-if="keyword.length > 0 && keyword.length < MIN_SEARCH">
          Nhập ít nhất 2 ký tự
        </b-form-text>
        <div class="d-flex flex-wrap mt-2">
          <span
            v-for="item in selected"
            :key="item.id"
            class="label label-lg label-light-primary label-inline mr-2 mb-2 text-break selected-chip"
          >
            {{ item.productCode }} - {{ item.productName }}
            <b-button
              variant="link"
              size="sm"
              class="p-0 ml-2"
              :disabled="isProcessing"
              :aria-label="`Bỏ ${item.productCode}`"
              @click="removeProduct(item)"
              ><i class="flaticon2-cross icon-xs"></i
            ></b-button>
          </span>
        </div>
      </b-col>
      <!-- FR-211: nguồn lấy từ API #3 (danh sách chọn hôm nay), bỏ danh mục chỉ tiêu đang mở. -->
      <b-col v-else cols="12" lg="6" class="mb-3">
        <label class="font-weight-bold mb-1">Danh mục chỉ tiêu nguồn</label>
        <div ref="firstInput">
          <treeselect
            :value="sourceId"
            :options="sourceOptions"
            :searchable="true"
            :clearable="true"
            :disabled="isProcessing"
            :placeholder="sourcesLoading ? 'Đang tải...' : 'Chọn danh mục chỉ tiêu nguồn'"
            noResultsText="Không có kết quả"
            noOptionsText="Không có kết quả"
            openDirection="bottom"
            @input="onSourceInput"
          />
        </div>
      </b-col>
      <b-col cols="12" sm="6" lg="3" class="mb-3">
        <label class="font-weight-bold mb-1">Ngày hiệu lực</label>
        <date-picker
          class="form-control form-control-sm"
          :config="dpEffectiveFrom"
          v-model="effectiveFrom"
          :disabled="isProcessing"
          @dp-change="clearPreview"
        ></date-picker>
        <!-- FR-208 (người dùng chốt 30/09/2026): gợi ý ngắn ngay dưới ô ngày + nút đổi ngày; di
             chuột / focus icon cảnh báo hiện câu SRS nguyên văn. -->
        <div v-if="monthEndSuggestion" class="month-end-hint mt-1">
          <i
            v-b-tooltip.hover.focus
            class="flaticon2-warning icon-sm text-warning mr-1 month-end-hint__icon"
            tabindex="0"
            role="img"
            :title="MONTH_END_HINT"
            :aria-label="MONTH_END_HINT"
          ></i>
          <span>Gần cuối tháng, chỉ tính doanh thu những ngày còn lại.</span>
          <div v-if="monthEndSuggestion.canSwitch" class="mt-1">
            <!-- light-primary (có sẵn, như ReviewFastInvoiceModal): rõ là nút bấm được, không lấn
                 nút chính "Xem trước" (người dùng góp ý link cũ quá mờ, 30/09/2026). -->
            <b-button
              variant="light-primary"
              size="sm"
              class="font-weight-bold py-1"
              :disabled="isProcessing"
              @click="switchToMonthStart"
              ><i class="flaticon2-calendar-1 icon-sm"></i> Đổi sang
              {{ monthEndSuggestion.date }}</b-button
            >
          </div>
        </div>
      </b-col>
      <!-- Bố cục (người dùng yêu cầu 30/09/2026): "Xem trước" nằm cùng hàng với ô nhập, ngay sau
           ô cuối người dùng điền; nhãn ẩn giữ nút thẳng hàng với ô nhập ở màn rộng. -->
      <b-col cols="12" sm="6" lg="3" class="mb-3">
        <label class="d-none d-sm-block mb-1">&nbsp;</label>
        <b-button
          size="sm"
          variant="primary"
          class="preview-btn"
          :disabled="!canPreview"
          @click="runPreview(1)"
        >
          <i class="flaticon-eye icon-sm"></i> Xem trước
          <b-spinner v-if="isSubmitting('preview')" small class="ml-2" />
        </b-button>
      </b-col>
    </b-row>


    <template v-if="preview">
      <!-- UX (người dùng chốt 30/09/2026): tóm tắt SRS 10.2 hiện thành ô số liệu, nhãn ngắn; câu
           SRS đầy đủ đặt ở tooltip của từng ô. -->
      <div class="d-flex flex-wrap mb-3">
        <div
          v-for="tile in summaryTiles"
          :key="tile.key"
          v-b-tooltip.hover
          :title="tile.hint || ''"
          class="summary-tile mr-2 mb-2"
          :class="tile.variant"
        >
          <div class="summary-tile__value">{{ tile.value }}</div>
          <div class="summary-tile__label">{{ tile.label }}</div>
        </div>
      </div>
      <!-- FR-211 / C19: khai tử không đổi nhánh ghi nhớ của nguồn (câu rút gọn, xem activeSourceWarning). -->
      <p v-if="summary.activeSourceCount > 0" class="text-warning font-weight-bold mb-3">
        <i class="flaticon2-warning icon-sm text-warning mr-1"></i>
        {{ activeSourceWarning(summary.activeSources) }}
      </p>

      <b-table
        :items="preview.dataset"
        :fields="previewFields"
        :tbody-tr-class="rowClass"
        bordered
        small
        responsive
        :busy="isSubmitting('preview')"
      >
        <!-- Khóa trong lúc xem trước đang chạy (người dùng yêu cầu 29/09/2026, rule 12 §9.1):
             tích liên tiếp trong 300 ms chờ vẫn được, nhưng khi request đã bay thì chờ kết quả
             rồi mới tích tiếp — không để request xem trước nối đuôi nhau. -->
        <template v-slot:cell(index)="row">
          {{ (preview.page - 1) * PREVIEW_PAGE_SIZE + row.index + 1 }}
        </template>
        <template v-slot:cell(exclude)="row">
          <b-form-checkbox
            :checked="excludeIds.includes(row.item.productId)"
            :disabled="isProcessing"
            @change="toggleExclude(row.item.productId, $event)"
          ></b-form-checkbox>
        </template>
        <template v-slot:cell(productCode)="row">
          <span class="text-break">{{ row.item.productCode }}</span>
        </template>
        <template v-slot:cell(productName)="row">
          <span class="text-break">{{ row.item.productName }}</span>
          <div>
            <span
              v-if="row.item.addedAsChildOf"
              class="label label-light-info label-inline mr-1 mt-1"
              >Mã con của {{ row.item.addedAsChildOf }}</span
            >
            <span
              v-if="row.item.droppedWithParent"
              class="label label-light-dark label-inline mr-1 mt-1"
              >Bỏ theo mã cha</span
            >
            <span
              v-if="row.item.deleted"
              class="label label-light-danger label-inline mt-1"
              >Đã xóa</span
            >
          </div>
        </template>
        <template v-slot:cell(nextEffectiveFrom)="row">
          <!-- AC-211-E3: "Đã có khai báo từ {ngày} thuộc {mã}" (dùng chung mọi cách thêm). -->
          <span v-if="row.item.nextEffectiveFrom">
            Đã có khai báo từ {{ toClientDate(row.item.nextEffectiveFrom) }} thuộc
            {{ row.item.nextCatTargetCode }}
          </span>
        </template>
      </b-table>
      <b-pagination
        v-if="preview.totalPage > 1"
        v-model="previewPage"
        :total-rows="preview.totalRow"
        :per-page="PREVIEW_PAGE_SIZE"
        :disabled="isProcessing"
        align="right"
        size="sm"
        @change="runPreview"
      ></b-pagination>

      <!-- Bổ sung 01 mục 2.5 (C30) -->
      <b-alert v-if="summary.pastPeriodFrom" show variant="danger" class="mt-3 py-2">
        <div class="font-weight-bold">
          Khai vào tháng đã qua ({{ summary.pastPeriodFrom }})
        </div>
        <b-form-checkbox
          v-model="confirmPastPeriod"
          class="mt-1"
          :disabled="isProcessing"
          >Tôi hiểu số liệu các tháng đã qua sẽ thay đổi</b-form-checkbox
        >
        <small class="d-block mt-1">
          Ngày hiệu lực thuộc tháng đã qua. Số liệu doanh thu, chỉ tiêu, thưởng của
          các tháng từ {{ summary.pastPeriodFrom }} sẽ thay đổi. Nếu muốn đổi cách
          chia nhóm, nên tạo danh mục chỉ tiêu mới.
        </small>
      </b-alert>

      <b-form-group v-if="summary.willMove > 0" class="mt-3">
        <label class="font-weight-bold"
          >Lý do chuyển danh mục chỉ tiêu (bắt buộc)</label
        >
        <b-form-textarea
          v-model="reason"
          rows="2"
          maxlength="500"
          :disabled="isProcessing"
        ></b-form-textarea>
      </b-form-group>
    </template>

    <div class="d-flex flex-wrap justify-content-end border-top pt-3 mt-3">
      <b-button
        size="sm"
        variant="secondary"
        class="mr-2 mb-1"
        :disabled="isProcessing"
        @click="$refs.modal.hide()"
        >Hủy</b-button
      >
      <SaveButton
        type="button"
        text="Xác nhận thêm"
        class="mb-1"
        :is-busy="isSubmitting('commit')"
        :disabled="!canConfirm"
        @click="onConfirm"
      />
    </div>
  </b-modal>
</template>

<script>
import debounce from 'debounce';
import moment from 'moment';
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import ApiService from '@/core/services/api.service';
import AutoSuggest from '@/view/base/auto-sugguest/AutoSuggest';
import SaveButton from '@/view/components/common/SaveButton.vue';
import submitLockMixin from '@/view/mixins/submit-lock.mixins';
import {
  makeToastFaile,
  makeToastSuccess,
  makeToastWarning,
  getApiErrorMessage,
} from '@/utils/common';
import {
  DP_CONFIG,
  DATE_FORMAT,
  YEAR_MONTH_DAY_FORMAT,
  formatSpecificDate,
  getAddDate,
  getCurrentDate,
} from '@/utils/date';
import {
  CAT_TARGET_DEBOUNCE_MS,
  CAT_TARGET_MAX_EFFECTIVE_DAYS_AHEAD,
  CAT_TARGET_MONTH_END_WARNING_DAYS,
} from '@/utils/constants';
import { CAT_TARGET_ADD_MODE, MERCHANT_TYPE } from '@/utils/enum';

// t102962 — hộp "Thêm sản phẩm vào danh mục chỉ tiêu" (SRS 10.2; FR-201…208, 212; Bổ sung 01
// C30). P5a: "Chọn từng sản phẩm"; P5b: "Thêm nhanh theo danh mục sản phẩm" (mặc định), ghi nhớ
// nhánh, cảnh báo cuối tháng.
const API = 'v2/cat-targets';
const MIN_SEARCH = 2;
const PREVIEW_PAGE_SIZE = 500;
// Lỗi mà chỉ một lần xem trước mới hiện được ô cần điền → tự xem trước lại. SRS 10.2 nêu hai mã
// đầu; mã C30 thêm vì ô tích "Tôi hiểu…" chỉ hiện sau khi xem trước (qua nửa đêm sang tháng mới
// giữa lúc xem trước và lúc ghi) — không thêm câu chữ mới.
const RE_PREVIEW_CODES = [
  'CAT_TARGET_REASON_REQUIRED',
  'CAT_TARGET_CONFLICT',
  'CAT_TARGET_PAST_PERIOD_CONFIRM_REQUIRED',
];
const TIMEOUT_MESSAGE =
  'Thao tác đang chạy lâu. Vui lòng đợi vài phút rồi bấm Xem trước lại, không bấm Xác nhận thêm lại.';

const today = () => getCurrentDate(DATE_FORMAT);
// RULE-19: mốc cuối của ô ngày, tính lại mỗi lần mở hộp; ô ngày và nút FR-208 dùng chung.
const maxEffectiveDate = () =>
  formatSpecificDate(
    getAddDate(CAT_TARGET_MAX_EFFECTIVE_DAYS_AHEAD),
    DATE_FORMAT,
    YEAR_MONTH_DAY_FORMAT,
  );

export default {
  mixins: [submitLockMixin],
  components: { AutoSuggest, SaveButton, Treeselect },
  props: {
    catTargetId: { type: [Number, String], required: true },
  },
  data() {
    return {
      MIN_SEARCH,
      PREVIEW_PAGE_SIZE,
      mode: CAT_TARGET_ADD_MODE.CATEGORY,
      modeOptions: [
        { value: CAT_TARGET_ADD_MODE.CATEGORY, text: 'Thêm nhanh theo danh mục sản phẩm' },
        { value: CAT_TARGET_ADD_MODE.PRODUCT, text: 'Chọn từng sản phẩm' },
        {
          value: CAT_TARGET_ADD_MODE.FROM_CAT_TARGET,
          text: 'Chuyển toàn bộ từ danh mục chỉ tiêu khác',
        },
      ],
      sourceOptions: [],
      sourcesLoading: false,
      sourceId: null,
      categoryOptions: [],
      categoriesLoading: false,
      categoryId: null,
      rememberCategory: false,
      searchText: '',
      searchState: '',
      searchSeq: 0,
      suggestions: [],
      selected: [],
      effectiveFrom: today(),
      preview: null,
      previewPage: 1,
      previewSeq: 0,
      // Có thao tác loại trừ chưa được xem trước lại (đang chờ 300 ms hoặc chờ lượt đang chạy).
      replayPending: false,
      excludeIds: [],
      reason: '',
      confirmPastPeriod: false,
      // RULE-19: 01/01/2015 … hôm nay + 365 (SRS mục 10)
      dpEffectiveFrom: {
        ...DP_CONFIG.date,
        showClear: false,
        minDate: '2015-01-01',
        maxDate: maxEffectiveDate(),
      },
      // FR-208 — câu SRS nguyên văn (AC-208-P), hiện ở tooltip icon cảnh báo.
      MONTH_END_HINT:
        'Ngày này chỉ còn vài ngày trong tháng. Danh mục chỉ tiêu mới sẽ chỉ nhận doanh thu của ' +
        'những ngày còn lại, trong khi chỉ tiêu tháng có thể đã giao cho cả tháng.',
      REMEMBER_HINT:
        'Sản phẩm mới tạo trong danh mục sản phẩm này sẽ được đề xuất vào danh mục chỉ tiêu này.',
      previewFields: [
        { key: 'index', label: 'STT', thStyle: { width: '5%', minWidth: '50px' }, tdClass: 'text-center' },
        { key: 'exclude', label: 'Loại trừ', thStyle: { width: '8%', minWidth: '70px' } },
        { key: 'productCode', label: 'Mã', thStyle: { width: '16%', minWidth: '110px' } },
        { key: 'productName', label: 'Tên sản phẩm', thStyle: { width: '38%', minWidth: '200px' } },
        {
          key: 'currentCatTargetCode',
          label: 'Danh mục chỉ tiêu hiện tại',
          thStyle: { width: '18%', minWidth: '130px' },
          tdClass: 'text-break',
        },
        {
          key: 'nextEffectiveFrom',
          label: 'Khai báo kế tiếp',
          thStyle: { width: '20%', minWidth: '140px' },
        },
      ],
    };
  },
  computed: {
    keyword() {
      return (this.searchText || '').trim();
    },
    // Khóa cả nhóm control khi đang xem trước hoặc ghi (rule 12 mục 9.1, 03-frontend §3).
    isProcessing() {
      return this.isSubmitting('preview') || this.isSubmitting('commit');
    },
    summary() {
      return this.preview ? this.preview.summary : {};
    },
    // Ô số liệu tóm tắt (SRS 10.2); ô phụ chỉ hiện khi > 0. `hint` = câu SRS gốc.
    summaryTiles() {
      const s = this.summary;
      const tiles = [
        {
          key: 'total',
          value: s.total,
          label: 'Tổng sản phẩm',
          hint: `${s.total} sản phẩm, ${s.willWrite} sẽ ghi, ${s.alreadyInTarget} đã thuộc danh mục chỉ tiêu này.`,
        },
        { key: 'willWrite', value: s.willWrite, label: 'Sẽ ghi', variant: 'is-primary' },
        { key: 'alreadyInTarget', value: s.alreadyInTarget, label: 'Đã thuộc' },
        {
          key: 'willMove',
          value: s.willMove,
          label: 'Sẽ chuyển nhóm',
          variant: s.willMove > 0 ? 'is-warning' : '',
          hint: `${s.willMove} sản phẩm đang thuộc danh mục chỉ tiêu khác sẽ chuyển sang danh mục chỉ tiêu này.`,
        },
      ];
      if (s.childCount > 0) {
        tiles.push({
          key: 'childCount',
          value: s.childCount,
          label: 'Mã con',
          hint: `${s.childCount} mã con được thêm theo sản phẩm cha.`,
        });
      }
      if (s.deletedCount > 0) {
        tiles.push({ key: 'deletedCount', value: s.deletedCount, label: 'Đã xóa' });
      }
      if (s.hasLaterRow > 0) {
        tiles.push({
          key: 'hasLaterRow',
          value: s.hasLaterRow,
          label: 'Có khai báo sau',
          hint: `${s.hasLaterRow} sản phẩm đã có khai báo sau ngày này; khai báo mới chỉ có hiệu lực đến ngày đó.`,
        });
      }
      return tiles;
    },
    isCategoryMode() {
      return this.mode === CAT_TARGET_ADD_MODE.CATEGORY;
    },
    isProductMode() {
      return this.mode === CAT_TARGET_ADD_MODE.PRODUCT;
    },
    isRetireMode() {
      return this.mode === CAT_TARGET_ADD_MODE.FROM_CAT_TARGET;
    },
    hasSelection() {
      if (this.isCategoryMode) {
        return this.categoryId !== null;
      }
      return this.isProductMode ? this.selected.length > 0 : this.sourceId !== null;
    },
    // Mã danh mục chỉ tiêu nguồn cho câu cảnh báo nhánh ghi nhớ (FR-211).
    sourceCode() {
      const source = this.sourceOptions.find((option) => option.id === this.sourceId);
      return source ? source.code : '';
    },
    canPreview() {
      return this.hasSelection && Boolean(this.effectiveFrom) && !this.isProcessing;
    },
    // FR-208 (AC-208-P/N/E): ngày rơi vào 3 ngày cuối tháng → gợi ý ngày 1 tháng sau; chỉ hiện
    // nút đổi khi ngày đó còn trong khoảng RULE-19.
    monthEndSuggestion() {
      const date = moment(this.effectiveFrom, DATE_FORMAT, true);
      if (!date.isValid()) {
        return null;
      }
      if (date.date() <= date.daysInMonth() - CAT_TARGET_MONTH_END_WARNING_DAYS) {
        return null;
      }
      const next = date.clone().add(1, 'month').startOf('month');
      const max = moment(this.dpEffectiveFrom.maxDate, YEAR_MONTH_DAY_FORMAT);
      return { date: next.format(DATE_FORMAT), canSwitch: !next.isAfter(max, 'day') };
    },
    // SRS 10.2: trạng thái nút theo lần xem trước MỚI NHẤT — còn thao tác loại trừ chưa xem trước
    // lại thì khóa; khóa khi đang lưu, có chuyển mà lý do rỗng, `willWrite = 0` và không tích ghi
    // nhớ nhánh (FR-207 ghi cả khi total = 0); Bổ sung 01: tháng đã qua mà chưa tích xác nhận.
    canConfirm() {
      if (!this.preview || this.isProcessing || this.replayPending) {
        return false;
      }
      if (this.summary.willWrite === 0 && !(this.isCategoryMode && this.rememberCategory)) {
        return false;
      }
      if (this.summary.willMove > 0 && !this.reason.trim()) {
        return false;
      }
      return !(this.summary.pastPeriodFrom && !this.confirmPastPeriod);
    },
  },
  created() {
    // Debounce riêng cho từng lần mở component (không dùng chung timer cấp module) để hủy được
    // khi đóng hộp / đổi dữ liệu.
    this.debouncedSearch = debounce(this.searchProducts, CAT_TARGET_DEBOUNCE_MS);
    this.debouncedReplay = debounce(this.replayPreview, CAT_TARGET_DEBOUNCE_MS);
  },
  beforeDestroy() {
    this.debouncedSearch.clear();
    this.debouncedReplay.clear();
  },
  methods: {
    show() {
      // FR-208: mặc định hôm nay, tính lại mỗi lần mở hộp (cùng mốc RULE-19).
      this.dpEffectiveFrom = { ...this.dpEffectiveFrom, maxDate: maxEffectiveDate() };
      this.effectiveFrom = today();
      this.fetchCategories();
      this.fetchSources();
      this.$refs.modal.show();
    },
    // Rule 12 §9.2: mở hộp thì focus ô nhập đầu tiên của cách thêm đang chọn.
    focusFirstInput() {
      const input = this.$refs.firstInput && this.$refs.firstInput.querySelector('input');
      if (input) {
        input.focus();
      }
    },
    // Cây nạp một lần cho mỗi lần dùng component; lỗi thì lần mở sau tải lại.
    fetchCategories() {
      if (this.categoryOptions.length || this.categoriesLoading) {
        return;
      }
      this.categoriesLoading = true;
      ApiService.query('category/get-list-category', {
        params: { page: 1, limit: 1000, name: '', code: '', allMerchant: true },
      })
        .then(({ data }) => {
          this.categoryOptions = data.data.list_caterogy;
        })
        .catch((err) => makeToastFaile(getApiErrorMessage(err)))
        .finally(() => {
          this.categoriesLoading = false;
        });
    },
    // FR-211: API #3 (hôm nay), bỏ danh mục chỉ tiêu đang mở; tải lại mỗi lần mở hộp vì danh sách
    // đổi theo ngày và theo thao tác tạo/xóa.
    fetchSources() {
      if (this.sourcesLoading) {
        return;
      }
      this.sourcesLoading = true;
      ApiService.query(`${API}/selectable`)
        .then(({ data }) => {
          this.sourceOptions = data.data
            .filter((item) => item.id !== Number(this.catTargetId))
            .map((item) => ({ id: item.id, code: item.code, label: `${item.code} - ${item.name}` }));
        })
        .catch((err) => makeToastFaile(getApiErrorMessage(err)))
        .finally(() => {
          this.sourcesLoading = false;
        });
    },
    // FR-211 — dùng ở xem trước và sau khi ghi. Người dùng chốt 30/09/2026: câu ngắn cho người
    // dùng cuối, nêu tên nhánh và ghi rõ "danh mục sản phẩm" (quy ước C28) để không nhầm với danh
    // mục chỉ tiêu hay tên sản phẩm; bỏ phần "nhờ vận hành gỡ nhánh bằng SQL" (nội bộ C19).
    activeSourceWarning(sources) {
      const names = (sources || [])
        .map((source) => `"${source.name || source.categoryId}"`)
        .join(', ');
      return (
        `Danh mục sản phẩm ${names} đang ghi nhớ cho ${this.sourceCode}: sản phẩm mới tạo ` +
        `trong danh mục sản phẩm này vẫn được tự đề xuất vào ${this.sourceCode}.`
      );
    },
    onSourceInput(value) {
      this.sourceId = value === undefined ? null : value;
      this.clearPreview();
    },
    // Nút lá có `children: []` → bỏ đi để treeselect coi là lá (không hiện mũi tên mở rỗng);
    // thêm hậu tố TGMC như các màn chọn danh mục khác (InventoryCheckAddModal).
    categoryNormalizer(node) {
      return {
        id: node.id,
        label: node.merchantId === MERCHANT_TYPE.TGMC ? `${node.name} (TGMC)` : node.name,
        children: node.children && node.children.length ? node.children : undefined,
      };
    },
    // vue-treeselect trả `undefined` khi bấm x (không phải null) → chuẩn hóa để `hasSelection`
    // khóa nút Xem trước đúng (review P5b #1).
    onCategoryInput(value) {
      this.categoryId = value === undefined ? null : value;
      this.clearPreview();
    },
    switchToMonthStart() {
      this.effectiveFrom = this.monthEndSuggestion.date;
      this.clearPreview();
    },
    // Chạy khi đóng hộp: dọn state và hủy mọi lời gọi đang chờ, để lần mở sau sạch.
    reset() {
      this.debouncedSearch.clear();
      this.searchSeq += 1;
      this.searchText = '';
      this.searchState = '';
      this.suggestions = [];
      this.selected = [];
      this.mode = CAT_TARGET_ADD_MODE.CATEGORY;
      this.categoryId = null;
      this.rememberCategory = false;
      this.sourceId = null;
      this.effectiveFrom = today();
      this.reason = '';
      this.clearPreview();
    },
    toClientDate(date) {
      return formatSpecificDate(date, YEAR_MONTH_DAY_FORMAT, DATE_FORMAT);
    },
    rowClass(item) {
      // UC-04: tô vàng sản phẩm sẽ chuyển; dòng loại trừ / bỏ theo mã cha làm mờ.
      if (!item) {
        return '';
      }
      if (item.excluded || item.droppedWithParent) {
        return 'text-muted';
      }
      return item.willMove ? 'table-warning' : '';
    },
    // Đổi cách chọn hoặc ngày → xóa xem trước (SRS 10.2) và bỏ mọi lần xem trước đang chờ.
    clearPreview() {
      this.debouncedReplay.clear();
      this.replayPending = false;
      this.previewSeq += 1;
      this.preview = null;
      this.previewPage = 1;
      this.excludeIds = [];
      this.confirmPastPeriod = false;
    },
    onSearchInput(text) {
      this.searchText = text || '';
      this.searchState = '';
      // Từ khóa đổi → bỏ kết quả của từ khóa cũ còn đang bay về.
      this.searchSeq += 1;
      if (this.keyword.length < MIN_SEARCH) {
        this.debouncedSearch.clear();
        this.suggestions = [];
        return;
      }
      this.debouncedSearch();
    },
    // FR-201: gọi API sau 300 ms ngừng gõ; chỉ nhận kết quả của lần gõ mới nhất.
    searchProducts() {
      const keyword = this.keyword;
      if (keyword.length < MIN_SEARCH) {
        return;
      }
      const seq = ++this.searchSeq;
      ApiService.query(`${API}/products/search`, { params: { keyword } })
        .then(({ data }) => {
          if (seq !== this.searchSeq) {
            return;
          }
          this.suggestions = data.data;
          this.searchState = data.data.length ? '' : 'empty';
        })
        .catch((err) => {
          if (seq === this.searchSeq) {
            makeToastFaile(getApiErrorMessage(err));
          }
        });
    },
    onSelectProduct(option) {
      this.debouncedSearch.clear();
      this.searchSeq += 1;
      if (option && option.item && !this.selected.some((item) => item.id === option.item.id)) {
        this.selected.push(option.item);
        this.clearPreview();
      }
      this.searchText = '';
      this.suggestions = [];
      this.searchState = '';
    },
    removeProduct(item) {
      this.selected = this.selected.filter((selectedItem) => selectedItem.id !== item.id);
      this.clearPreview();
    },
    // Đúng một cách chọn (AC-202-N1).
    requestBody() {
      let selection = { productIds: this.selected.map((item) => item.id) };
      if (this.isCategoryMode) {
        selection = { categoryId: this.categoryId };
      } else if (this.isRetireMode) {
        selection = { fromCatTargetId: this.sourceId };
      }
      return {
        ...selection,
        excludeProductIds: this.excludeIds,
        effectiveFrom: formatSpecificDate(this.effectiveFrom, DATE_FORMAT, YEAR_MONTH_DAY_FORMAT),
      };
    },
    // FR-202 — xem trước; chỉ nhận kết quả của lần gọi mới nhất.
    runPreview(page = 1) {
      if (this.isSubmitting('preview')) {
        // Đang có lượt xem trước: không làm lượt đó thành "cũ" (sẽ bị bỏ) mà xếp lượt mới chạy
        // ngay sau khi lượt đang bay xong.
        this.replayPending = true;
        return;
      }
      if (!this.canPreview) {
        return;
      }
      const seq = ++this.previewSeq;
      this.replayPending = false;
      return this.withSubmitLock('preview', async () => {
        try {
          const { data } = await ApiService.post(`${API}/${this.catTargetId}/members/preview`, {
            ...this.requestBody(),
            page,
            limit: PREVIEW_PAGE_SIZE,
          });
          if (seq !== this.previewSeq) {
            return;
          }
          this.preview = data.data;
          this.previewPage = data.data.page;
        } catch (err) {
          if (seq === this.previewSeq) {
            this.preview = null;
            makeToastFaile(getApiErrorMessage(err));
          }
        }
      }).then(() => {
        if (this.replayPending) {
          this.replayPreview();
        }
      });
    },
    replayPreview() {
      this.runPreview(this.previewPage);
    },
    toggleExclude(productId, checked) {
      this.excludeIds = checked
        ? [...this.excludeIds, productId]
        : this.excludeIds.filter((id) => id !== productId);
      // SRS 10.2: gọi lại xem trước sau 300 ms thao tác cuối, giữ trang; trong lúc chờ khóa nút
      // "Xác nhận thêm" để không gửi loại trừ mới kèm dấu xem trước cũ.
      this.replayPending = true;
      this.debouncedReplay();
    },
    onConfirm() {
      if (!this.canConfirm) {
        return;
      }
      let shouldRePreview = false;
      return this.withSubmitLock('commit', async () => {
        try {
          const { data } = await ApiService.post(`${API}/${this.catTargetId}/members`, {
            ...this.requestBody(),
            // Ô lý do chỉ hiện khi có sản phẩm chuyển nhóm; ô đã ẩn (vd vừa loại trừ hết sản phẩm
            // chuyển nhóm) thì không gửi lý do cũ, tránh ghi vào sản phẩm không chuyển nhóm.
            reason: this.summary.willMove > 0 ? this.reason : '',
            rememberCategory: this.isCategoryMode && this.rememberCategory,
            confirmPastPeriod: this.confirmPastPeriod,
            previewHash: this.summary.previewHash,
          });
          makeToastSuccess(data.message);
          // FR-211: "xem trước và kết quả" đều nhắc nhánh ghi nhớ còn lại của nguồn (review P5c #4).
          if (data.data && data.data.activeSourceCount > 0) {
            makeToastWarning(this.activeSourceWarning(data.data.activeSources));
          }
          this.$emit('saved');
          this.$refs.modal.hide();
        } catch (err) {
          const response = err && err.response;
          if (!response || response.status === 504) {
            makeToastFaile(TIMEOUT_MESSAGE);
            return;
          }
          makeToastFaile(getApiErrorMessage(err));
          // Khai tử ghi theo lô (kỹ thuật #4): lỗi giữa chừng thì các lô trước đã ghi → luôn xem
          // trước lại để bảng phản ánh phần còn lại (review P5c #1).
          shouldRePreview =
            this.isRetireMode || RE_PREVIEW_CODES.includes(response.data && response.data.code);
        }
      }).then(() => {
        // Xem trước lại SAU khi khóa 'commit' đã nhả: gọi trong lúc còn khóa thì canPreview = false,
        // bảng và previewHash giữ bản cũ → bấm Xác nhận lại sẽ CONFLICT mãi.
        if (shouldRePreview) {
          this.runPreview(this.previewPage);
        }
      });
    },
  },
};
</script>

<style scoped>
.preview-btn {
  min-height: 32px;
}
.month-end-hint__icon {
  cursor: help;
}
.month-end-hint {
  font-size: 0.85rem;
  color: #b58105;
}
.summary-tile {
  min-width: 110px;
  padding: 0.5rem 0.75rem;
  border: 1px solid #ebedf3;
  border-radius: 0.42rem;
  background: #f3f6f9;
  text-align: center;
}
.summary-tile__value {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.2;
}
.summary-tile__label {
  font-size: 0.85rem;
  color: #7e8299;
}
.summary-tile.is-primary {
  border-color: #c9e4ff;
  background: #e1f0ff;
}
.summary-tile.is-primary .summary-tile__value {
  color: #3699ff;
}
.summary-tile.is-warning {
  border-color: #ffe7a8;
  background: #fff4de;
}
.summary-tile.is-warning .summary-tile__value {
  color: #ffa800;
}
.selected-chip {
  height: auto;
  white-space: normal;
  text-align: left;
}
</style>
